import type { SafeHtml } from '@angular/platform-browser';
import type { AnimationEvent } from '@angular/animations';
import type { Observable } from 'rxjs';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    type OnDestroy,
    type OnInit,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { LayoutFacade } from '@web/store/layout/layout.facade';

import SiteActions from '@web/store/website/site/site.actions';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'web-preview',
    templateUrl: './preview.component.html',
    styleUrls: ['./preview.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('openClose', [
            state(
                'open',
                style({
                    position: 'fixed',
                    zIndex: 1029,
                    height: '100%',
                    width: '100%',
                    paddingTop: '64px',
                    background: '#F2F5F5',
                })
            ),
            state(
                'closed',
                style({
                    overflow: 'hidden',
                    position: 'absolute',
                    zIndex: 1029,
                    height: '0',
                    width: '100%',
                    paddingTop: '64px',
                })
            ),
            transition('open <=> closed', animate(300)),
        ]),
    ],
    imports: [AsyncPipe],
})
export class PreviewComponent implements OnInit, OnDestroy {
    private readonly actions$: Actions = inject(Actions);
    private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    public showPreview: boolean = false;
    public html: SafeHtml;

    public readonly isPreview$: Observable<boolean> = this.layoutFacade.isPreview$;
    public readonly isPreviewMobile$: Observable<boolean> = this.layoutFacade.isPreviewMobile$;
    public readonly isPreviewTablet$: Observable<boolean> = this.layoutFacade.isPreviewTablet$;

    private readonly unsubscribe$ = new Subject<void>();

    public ngOnInit(): void {
        this.actions$
            .pipe(
                ofType(SiteActions.previewSiteSuccess),
                takeUntil(this.unsubscribe$),
                map(({ content }) => content)
            )
            .subscribe((content) => {
                this.html = this.sanitizer.bypassSecurityTrustHtml(content);
                this.changeDetectorRef.detectChanges(); // TODO: remove, make with reactive form mb?
            });
    }

    public animationEvent(event: AnimationEvent): void {
        if (event.fromState === 'open' && event.toState === 'closed') {
            this.html = null;
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
